import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import configService, { 
  Region, 
  CreateRegionDto, 
  UpdateRegionDto, 
  RegionFilters,
  PaginatedResponse 
} from '@/services/configService'

interface RegionState {
  regions: Region[]
  currentRegion: Region | null
  loading: boolean
  error: string | null
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
  filters: RegionFilters

  // Actions
  setFilters: (filters: RegionFilters) => void
  fetchRegions: (filters?: RegionFilters) => Promise<void>
  fetchRegionById: (id: string) => Promise<void>
  createRegion: (data: CreateRegionDto) => Promise<Region>
  updateRegion: (id: string, data: UpdateRegionDto) => Promise<Region>
  deleteRegion: (id: string) => Promise<void>
  clearError: () => void
  clearCurrentRegion: () => void
}

const useRegionStore = create<RegionState>()(
  devtools(
    (set, get) => ({
      regions: [],
      currentRegion: null,
      loading: false,
      error: null,
      pagination: {
        page: 1,
        limit: 10,
        total: 0,
        pages: 0
      },
      filters: {
        page: 1,
        limit: 10
      },

      setFilters: (filters) => {
        set({ filters: { ...get().filters, ...filters } })
      },

      fetchRegions: async (filters = {}) => {
        try {
          set({ loading: true, error: null })
          const currentFilters = { ...get().filters, ...filters }
          const response: PaginatedResponse<Region> = await configService.regions.getAll(currentFilters)
          
          set({
            regions: response.data,
            pagination: {
              page: currentFilters.page || 1,
              limit: currentFilters.limit || 10,
              total: response.total,
              pages: Math.ceil(response.total / (currentFilters.limit || 10))
            },
            filters: currentFilters,
            loading: false
          })
        } catch (error: any) {
          set({
            error: error.response?.data?.error || 'Erreur lors du chargement des régions',
            loading: false
          })
        }
      },

      fetchRegionById: async (id: string) => {
        try {
          set({ loading: true, error: null })
          const region = await configService.regions.getById(id)
          set({ currentRegion: region, loading: false })
        } catch (error: any) {
          console.error('Error fetching region by ID:', error)
          const errorMessage = error.response?.data?.error || 
                             error.response?.data?.message || 
                             error.message || 
                             'Erreur lors du chargement de la région'
          set({
            error: errorMessage,
            loading: false
          })
        }
      },

      createRegion: async (data: CreateRegionDto) => {
        try {
          set({ loading: true, error: null })
          const region = await configService.regions.create(data)
          
          // Add to regions list
          set((state) => ({
            regions: [region, ...state.regions],
            loading: false
          }))

          return region
        } catch (error: any) {
          set({
            error: error.response?.data?.error || 'Erreur lors de la création de la région',
            loading: false
          })
          throw error
        }
      },

      updateRegion: async (id: string, data: UpdateRegionDto) => {
        try {
          set({ loading: true, error: null })
          const region = await configService.regions.update(id, data)
          
          // Update in regions list
          set((state) => ({
            regions: state.regions.map(r => 
              r.id === id ? region : r
            ),
            currentRegion: state.currentRegion?.id === id ? region : state.currentRegion,
            loading: false
          }))

          return region
        } catch (error: any) {
          set({
            error: error.response?.data?.error || 'Erreur lors de la mise à jour de la région',
            loading: false
          })
          throw error
        }
      },

      deleteRegion: async (id: string) => {
        try {
          set({ loading: true, error: null })
          await configService.regions.delete(id)
          
          // Remove from regions list
          set((state) => ({
            regions: state.regions.filter(r => r.id !== id),
            currentRegion: state.currentRegion?.id === id ? null : state.currentRegion,
            loading: false
          }))
        } catch (error: any) {
          console.error('Error deleting region:', error)
          const errorMessage = error.response?.data?.error || 
                             error.response?.data?.message || 
                             error.message || 
                             'Erreur lors de la suppression de la région'
          set({
            error: errorMessage,
            loading: false
          })
          throw error
        }
      },

      clearError: () => set({ error: null }),
      clearCurrentRegion: () => set({ currentRegion: null })
    }),
    { name: 'RegionStore' }
  )
)

export default useRegionStore
